import { useState } from 'react';
import { ButtonBase, Typography } from '@mui/material';
import DateRange from '@mui/icons-material/DateRange';
import { FOCUS_RING } from '../../../theme/tokens';
import type { DateRangeFilter } from './dateRange';
import { DateRangeFilterDialog } from './DateRangeFilterDialog';

interface DateRangeControlProps {
  /** The currently-selected range. */
  value: DateRangeFilter;
  /** Called with the new range when the user picks a preset or edits a custom range. */
  onChange: (filter: DateRangeFilter) => void;
  /** Contextual copy for the dialog — which surface the range filters. */
  scopeLabel?: string;
  'data-testid'?: string;
}

/**
 * Shared date-range trigger: an accent-tinted chip showing the active range that
 * opens the preset dialog. Lifted verbatim (visually) from the Dashboard header
 * so the Dashboard and Reports surfaces share one control. The dialog's open
 * state lives here so each call site only owns its `{value, onChange}`.
 *
 * Rendered as a `ButtonBase` (not a bare `Box`) so it is keyboard-focusable and
 * announces itself as a dialog trigger.
 */
export function DateRangeControl({
  value,
  onChange,
  scopeLabel,
  'data-testid': testId,
}: DateRangeControlProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ButtonBase
        data-testid={testId}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`Change date range — currently ${value.label}`}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          backgroundColor: theme.palette.accent.tint,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          '&.Mui-focusVisible': { boxShadow: FOCUS_RING(theme.palette.primary.main) },
        })}
      >
        <DateRange sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value.label} ({value.startDate.toLocaleDateString()} - {value.endDate.toLocaleDateString()})
        </Typography>
      </ButtonBase>
      <DateRangeFilterDialog
        open={open}
        onClose={() => setOpen(false)}
        dateRangeFilter={value}
        onChange={onChange}
        scopeLabel={scopeLabel}
      />
    </>
  );
}

export default DateRangeControl;
